import type { StreamAnalytics } from '@vlabs/api/artemy/model';
import type { TOption } from '@vlabs/uikit/types';
import i18next from 'i18next';

// ponytail: пока доступен только один тип ВА — «Лица и тела» (facestream).
// При появлении новых типов список расширяется здесь.
// label — геттер: резолвится в момент рендера, поэтому реагирует на смену языка.
export const ANALYTICS_TYPES: TOption[] = [
  {
    value: 'facestream',
    get label(): string {
      return i18next.t('entities:sources.analytics_types.facestream');
    },
  },
];

// Известный тип — перевод, неизвестный — сам type (фолбэк).
export function getAnalyticsTypeLabel(type: string): string {
  return ANALYTICS_TYPES.find((option) => option.value === type)?.label ?? type;
}

const LABEL_PATTERN = /^(\D+?)(\d+)$/;

export interface AnalyticsLabelParts {
  type: string;
  number: number;
}

export function parseAnalyticsLabel(label: string | null | undefined): AnalyticsLabelParts | null {
  if (!label) return null;
  const match = LABEL_PATTERN.exec(label);
  if (!match) return null;

  return {
    type: match[1],
    number: Number(match[2]),
  };
}

// Номер для нового label — максимум среди существующих ВА того же типа + 1.
// После удаления ВА номера не переиспользуются (fire1, fight2 → fire2).
export function getMaxAnalyticsLabelNumber(
  existing: { label?: string }[],
  type: string,
): number {
  let max = 0;

  existing.forEach((analytics) => {
    const parts = parseAnalyticsLabel(analytics.label);
    if (parts?.type === type && parts.number > max) {
      max = parts.number;
    }
  });

  return max;
}

export function nextAnalyticsLabel(existing: { label?: string }[], type: string): string {
  return `${type}${getMaxAnalyticsLabelNumber(existing, type) + 1}`;
}

// Полные дефолты ВА при добавлении — по примеру createStream из документации.
// handler_id обязателен для бэка — указывается при добавлении ВА.
export function buildDefaultStreamAnalytics(type: string, label: string, handlerId: string): StreamAnalytics {
  return {
    type,
    label,
    parameters: {
      parameters: {
        mode: 1,
        enabled: true,
        frame_processing_mode: 'auto',
        real_time_mode_fps: 0,
        ffmpeg_threads_number: 0,
        preferred_program_stream_frame_width: 800,
        roi: {
          x: 0, y: 0, width: 100, height: 100, mode: 'percent',
        },
        droi: {
          x: 0, y: 0, width: 100, height: 100, mode: 'percent',
        },
        sending: {
          body: {
            send_only_full_set: true,
            delete_track_after_sending: false,
          },
          bestshot_settings: {
            type: 1,
            face_bestshots_to_send: 1,
            body_bestshots_to_send: 1,
          },
          full_frame_settings: 1,
          time_period_of_searching: -1,
          silent_period: 0,
          type: 'sec',
        },
        filtering: {
          min_score_face: 0.5187,
          min_score_body: 0.5,
          detection_yaw_threshold: 40,
          detection_pitch_threshold: 40,
          detection_roll_threshold: 30,
          yaw_number: 1,
          yaw_collection_mode: false,
          mouth_occlusion_threshold: 0,
          min_body_size_threshold: 0,
        },
        liveness: {
          livenesses_weights: [0, 0],
          use_mask_liveness_filtration: false,
          use_flying_faces_liveness_filtration: false,
          liveness_mode: 0,
          number_of_liveness_checks: 0,
          liveness_threshold: 0,
          mask_backgrounds_count: 0,
        },
        healthcheck: { period: 3600 },
        primary_track_policy: {
          use_primary_track_policy: false,
          best_shot_min_size: 70,
          best_shot_proper_size: 140,
        },
        event_handler: { type: 'luna_handler', handler_id: handlerId },
      },
    },
  };
}
